import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// material
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Link,
  Stack
} from '@mui/material';
// hooks
import useAuth from '../hooks/useAuth';
// api
import { acceptPolicies } from '../api';
// config
import { TERMS_VERSION, PRIVACY_VERSION } from '../config/legal';

// ----------------------------------------------------------------------
// Blocking gate that prompts a signed-in user to accept the current Terms of
// Service / Privacy Policy when they have never accepted them, or when the
// version they last accepted is out of date. Acceptance (version + timestamp)
// is recorded against their user record via POST /accept-policies.
// ----------------------------------------------------------------------

export default function PolicyAcceptanceModal() {
  const { resiviewUser, refreshResiviewUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { needsTerms, needsPrivacy } = useMemo(() => {
    if (!resiviewUser) return { needsTerms: false, needsPrivacy: false };
    return {
      needsTerms: resiviewUser.acceptedTermsVersion !== TERMS_VERSION,
      needsPrivacy: resiviewUser.acceptedPrivacyVersion !== PRIVACY_VERSION
    };
  }, [resiviewUser]);

  const open = Boolean(resiviewUser) && (needsTerms || needsPrivacy);

  // First-time acceptance (never accepted anything) reads differently from an
  // update to a previously-accepted policy.
  const isFirstTime = resiviewUser && !resiviewUser.acceptedTermsVersion && !resiviewUser.acceptedPrivacyVersion;

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await acceptPolicies({
        ...(needsTerms && { termsVersion: TERMS_VERSION }),
        ...(needsPrivacy && { privacyVersion: PRIVACY_VERSION })
      });
      await refreshResiviewUser();
      setAgreed(false);
      enqueueSnackbar('Thank you — your acceptance has been recorded.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Something went wrong recording your acceptance. Please try again.', {
        variant: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const bothNeeded = needsTerms && needsPrivacy;
  let subject = 'Privacy Policy';
  if (bothNeeded) subject = 'Terms of Service and Privacy Policy';
  else if (needsTerms) subject = 'Terms of Service';

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown aria-labelledby="policy-acceptance-title">
      <DialogTitle id="policy-acceptance-title">
        {isFirstTime ? `Welcome — please review our ${subject}` : `We've updated our ${subject}`}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {isFirstTime
            ? `Before you continue, please review and accept ResiView's ${subject}.`
            : `We've made changes to our ${subject}. Please review and accept the updated ${
                bothNeeded ? 'documents' : 'document'
              } to continue using ResiView.`}
        </Typography>
        <Stack spacing={1} sx={{ mb: 1 }}>
          {needsTerms && (
            <Link component={RouterLink} to="/terms" target="_blank" rel="noopener" underline="hover">
              Read the Terms of Service
            </Link>
          )}
          {needsPrivacy && (
            <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener" underline="hover">
              Read the Privacy Policy
            </Link>
          )}
        </Stack>
        <FormControlLabel
          control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
          label={`I have read and agree to the ${subject}.`}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" disabled={!agreed || submitting} onClick={handleAccept}>
          {submitting ? 'Saving…' : 'Accept & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
